/**
 * MovementModeToggle - the follow/free segmented pill (bottom-center),
 * shown after onboarding on touch-ish setups. React port of
 * multiplayerUI's showMovementModeToggle; state still lives in
 * src/movementMode.js - this is a view over it.
 */
import { useSyncExternalStore } from 'react';
import { useStore } from 'zustand';
import { Footprints, Joystick } from 'lucide';
import { getMovementMode, setMovementMode, onMovementModeChange } from '../../movementMode.js';
import { hudStore } from '../../state/hudStore';
import { Icon } from './Icon';
import type { LucideIconNode } from '../../utils/iconCanvas';

type Mode = 'free' | 'follow';

function Segment({
  icon,
  label,
  mode,
  active,
}: {
  icon: LucideIconNode;
  label: string;
  mode: Mode;
  active: boolean;
}) {
  return (
    <button
      type="button"
      data-mode={mode}
      onClick={() => setMovementMode(mode)}
      style={{
        border: 'none',
        borderRadius: '999px',
        padding: '6px 14px',
        fontSize: '13px',
        cursor: 'pointer',
        background: active ? '#7c3aed' : 'transparent',
        color: active ? '#fff' : '#9ca3af',
        fontWeight: active ? 600 : 400,
        transition: 'background 0.15s, color 0.15s',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
      }}
    >
      <Icon icon={icon} size={16} />
      {label}
    </button>
  );
}

export function MovementModeToggle() {
  const visible = useStore(hudStore, (s) => s.movementToggleVisible);
  const mode = useSyncExternalStore(onMovementModeChange, getMovementMode) as Mode;
  if (!visible) return null;

  return (
    <div
      id="movement-mode-toggle"
      style={{
        display: 'flex',
        background: 'rgba(17,24,39,0.92)',
        borderRadius: '999px',
        padding: '3px',
        fontFamily: 'sans-serif',
        boxShadow: '0 2px 12px rgba(0,0,0,0.35)',
        pointerEvents: 'auto',
      }}
    >
      <Segment
        icon={Footprints as unknown as LucideIconNode}
        label="Follow Me"
        mode="follow"
        active={mode === 'follow'}
      />
      <Segment
        icon={Joystick as unknown as LucideIconNode}
        label="Free Movement"
        mode="free"
        active={mode === 'free'}
      />
    </div>
  );
}
